import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { BRAND, statusColorOf } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { fmtDateLong } from "@/lib/format";
import {
  barDates,
  clampTitleWidth,
  TIMELINE_TITLE_DEFAULT,
  timelineDensity,
} from "@/lib/timelineScale";
import { StatusPill } from "@/components/ui/StatusPill";
import { TicketAgeChip, TicketIdChip } from "@/components/board/TicketChips";
import {
  isTicketBoard,
  ticketLabel,
  type ViewBoard,
  type ViewRow,
  type ViewStatusOption,
} from "@/lib/boardView";
import { useBoardStatusSegments } from "@/hooks/useBoardStatusSegments";
import type { StatusSegment, UUID } from "@/lib/types";

interface TimelineViewProps {
  board: ViewBoard;
  onRowClick: (row: ViewRow) => void;
  today?: string;
}

const DAY_MS = 86_400_000;

// Monday-first weekday labels for the day-scale header (index = (getDay()+6)%7).
const DOW = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

// Human duration of one cycle-time segment (German), coarsened to the unit that
// reads best: minutes < 1h, hours < 2 days, else days.
function fmtSegDur(startIso: string, endIso: string): string {
  const ms = Math.max(0, new Date(endIso).getTime() - new Date(startIso).getTime());
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "< 1 Min.";
  if (mins < 60) return `${mins} Min.`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} Std.`;
  return `${Math.round(hrs / 24)} Tage`;
}

// The title column width is user-resizable, persisted per browser (shared across
// all timeline boards, like the detail-pane ratio).
const TITLE_W_KEY = "flow:timeline-title-w";
function readTitleW(): number {
  try {
    const v = localStorage.getItem(TITLE_W_KEY);
    return v != null ? clampTitleWidth(Number(v)) : TIMELINE_TITLE_DEFAULT;
  } catch {
    return TIMELINE_TITLE_DEFAULT;
  }
}
function persistTitleW(px: number): void {
  try {
    localStorage.setItem(TITLE_W_KEY, String(px));
  } catch {
    /* ignore */
  }
}

const isoDay = (s: string): number => new Date(`${s.slice(0, 10)}T00:00:00Z`).getTime();

// ISO-8601 week number (Monday-based — matches the timeline's Monday week start).
function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3); // Thursday of week 1
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
}

export function TimelineView({ board, onRowClick, today }: TimelineViewProps) {
  // Real today (was hardcoded to a fixture date) — overridable for tests.
  const todayIso = today ?? new Date().toISOString().slice(0, 10);
  const todayDate = useMemo(() => new Date(`${todayIso}T00:00:00Z`), [todayIso]);
  // Tickets anchor on their creation date (open-since is the helpdesk axis);
  // project boards keep anchoring on the due date.
  const ticketMode = isTicketBoard(board);
  // Cycle-time segments (time-in-each-status) for the bar — ticket boards only.
  const statusSegments = useBoardStatusSegments(board.id as UUID, ticketMode);
  // Hovered bar → portaled detail card. Captures the bar's screen rect at enter
  // so the card can anchor itself with fixed positioning (escaping the scroll
  // container's overflow clip).
  const [hover, setHover] = useState<{ row: ViewRow; rect: DOMRect } | null>(null);
  // A live update can unmount the hovered bar without firing onMouseLeave, which
  // would strand a ghost tooltip anchored to the old position. Drop it when the
  // hovered row leaves the board.
  useEffect(() => {
    setHover((h) => (h && board.rows.some((r) => r.id === h.row.id) ? h : null));
  }, [board.rows]);
  const [titleColW, setTitleColW] = useState(readTitleW);
  const rowH = 36;

  // Drag the title-column's right edge to resize it (persisted on release). The
  // date area is available = containerW − titleColW, so widening the title column
  // reflows the bars automatically.
  const startTitleResize = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = titleColW;
    const width = (ev: globalThis.MouseEvent) => clampTitleWidth(startW + (ev.clientX - startX));
    const onMove = (ev: globalThis.MouseEvent) => setTitleColW(width(ev));
    const onUp = (ev: globalThis.MouseEvent) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      persistTitleW(width(ev));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
  };

  // Measure the scroll area so columns can stretch to fill the available width
  // when the date span is short (otherwise the timeline clusters on the left and
  // leaves the right side empty).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [containerW, setContainerW] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const range = useMemo(() => {
    const anchors: number[] = [];
    board.rows.forEach((r) => {
      // Keep both ends of every bar on-axis (Zeitraum range, ticket lifetime, …).
      // Guard finiteness: a malformed date cell (e.g. an imported "TBD") must
      // drop that anchor, not turn range.start into Invalid Date and blank the
      // ENTIRE timeline. (The bars memo already guards its own span.)
      const { startIso, endIso } = barDates(r, ticketMode, todayIso);
      const pushDay = (iso: string | null) => {
        if (!iso) return;
        const d = isoDay(iso);
        if (Number.isFinite(d)) anchors.push(d);
      };
      pushDay(startIso);
      pushDay(endIso);
    });
    if (anchors.length === 0) {
      const start = new Date(todayDate);
      start.setDate(start.getDate() - 7);
      const end = new Date(todayDate);
      end.setDate(end.getDate() + 21);
      return { start, end };
    }
    let min = Infinity;
    let max = -Infinity;
    anchors.forEach((a) => {
      min = Math.min(min, a);
      max = Math.max(max, a);
    });
    const s = new Date(min - 7 * DAY_MS);
    const e = new Date(max + 14 * DAY_MS);
    while (s.getDay() !== 1) s.setDate(s.getDate() - 1);
    return { start: s, end: e };
  }, [board.rows, todayDate, todayIso, ticketMode]);

  const totalDays = Math.ceil((range.end.getTime() - range.start.getTime()) / DAY_MS);

  // Auto-density from the data span — no manual zoom. Week (KW) columns for the
  // usual span; month columns once it's long enough that KW ticks would crowd.
  // dayWidth fills the canvas, clamped per scale.
  const available = containerW - titleColW;
  const { scale, dayWidth } = timelineDensity(totalDays, available);
  // Day scale stacks a third header row (Monat / KW / Tag); the others are two.
  const headerH = scale === "day" ? 80 : 56;

  const months = useMemo(() => {
    const out: { label: string; short: string; year: number; startOffset: number; days: number }[] =
      [];
    let cur = new Date(range.start);
    while (cur < range.end) {
      const monthStart = new Date(cur.getFullYear(), cur.getMonth(), 1);
      const nextMonth = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const segStart = cur.getTime() > monthStart.getTime() ? new Date(cur) : monthStart;
      const segEnd = nextMonth < range.end ? nextMonth : range.end;
      const startOffset = Math.round((segStart.getTime() - range.start.getTime()) / DAY_MS);
      const days = Math.round((segEnd.getTime() - segStart.getTime()) / DAY_MS);
      out.push({
        label: monthStart.toLocaleDateString("de-DE", { month: "long", year: "numeric" }),
        short: monthStart.toLocaleDateString("de-DE", { month: "short" }),
        year: monthStart.getFullYear(),
        startOffset,
        days,
      });
      cur = nextMonth;
    }
    return out;
  }, [range]);

  // Year bands for the month-scale top header (coalesce consecutive months).
  const years = useMemo(() => {
    const out: { label: string; days: number }[] = [];
    months.forEach((m) => {
      const last = out[out.length - 1];
      if (last && last.label === String(m.year)) last.days += m.days;
      else out.push({ label: String(m.year), days: m.days });
    });
    return out;
  }, [months]);

  const days = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < totalDays; i += 1) {
      const d = new Date(range.start);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [range, totalDays]);

  // Week columns for the Woche/Monat header. range.start is snapped to a Monday,
  // so every 7-day chunk begins on Monday; the last chunk may be a partial week.
  const weeks = useMemo(() => {
    const out: { label: string; days: number }[] = [];
    for (let i = 0; i < totalDays; i += 7) {
      const d = days[i];
      if (!d) break;
      out.push({ label: `KW ${isoWeek(d)}`, days: Math.min(7, totalDays - i) });
    }
    return out;
  }, [days, totalDays]);

  const statusCol = board.columns.find((c) => c.type === "status");

  // A bar spans A→B: an explicit Zeitraum (timeline) cell, else a ticket's
  // creation→resolved lifetime (open tickets pin on creation), else a project/
  // personal row's single due date. Rows with no date don't plot.
  const bars = board.rows
    .map((r) => {
      const { startIso, endIso } = barDates(r, ticketMode, todayIso);
      if (!startIso) return null;
      const offsetDays = Math.round((isoDay(startIso) - range.start.getTime()) / DAY_MS);
      const endOffset = endIso
        ? Math.round((isoDay(endIso) - range.start.getTime()) / DAY_MS)
        : offsetDays;
      const span = endOffset - offsetDays + 1;
      return { row: r, offsetDays, span: Number.isFinite(span) ? Math.max(1, span) : 1 };
    })
    .filter((b): b is { row: ViewRow; offsetDays: number; span: number } => Boolean(b));

  const todayOffset = Math.round((todayDate.getTime() - range.start.getTime()) / DAY_MS);

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px 12px",
          borderBottom: "1px solid var(--hairline-soft)",
        }}
      >
        <div style={{ flex: 1 }}></div>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{t.rowsWithDate}</span>
      </div>

      <div
        ref={scrollRef}
        onScroll={() => setHover(null)}
        style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", position: "relative" }}
      >
        <div
          style={{
            width: titleColW,
            flexShrink: 0,
            position: "sticky",
            left: 0,
            zIndex: 3,
            background: "var(--surface)",
            borderRight: "1px solid var(--hairline)",
          }}
        >
          {/* Drag the right edge to resize the title column. */}
          <div
            onMouseDown={startTitleResize}
            title={t.timelineResizeTitleCol}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              right: -3,
              width: 6,
              cursor: "col-resize",
              zIndex: 4,
            }}
          />
          <div
            style={{
              height: headerH,
              borderBottom: "1px solid var(--hairline)",
              padding: "8px 12px",
              display: "flex",
              alignItems: "flex-end",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {board.columns[0]?.name ?? "Titel"}
          </div>
          {board.rows.map((r) => {
            const opt = statusCol?.options?.find((o) => o.id === r.status);
            const tint = statusColorOf(opt?.color);
            return (
              <div
                key={r.id}
                onClick={() => onRowClick(r)}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                style={{
                  height: rowH,
                  padding: "0 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  borderBottom: "1px solid var(--hairline-soft)",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 99,
                    background: tint.dot,
                    flexShrink: 0,
                  }}
                ></span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    color: "var(--ink-1)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.title}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ minWidth: totalDays * dayWidth, position: "relative" }}>
          <div
            style={{
              display: "flex",
              height: 28,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "var(--surface)",
              borderBottom: "1px solid var(--hairline-soft)",
            }}
          >
            {(scale === "month" ? years : months).map((seg, i) => (
              <div
                key={i}
                style={{
                  width: seg.days * dayWidth,
                  flexShrink: 0,
                  padding: "6px 10px",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--ink-2)",
                  borderRight: "1px solid var(--hairline)",
                  textTransform: "capitalize",
                }}
              >
                {seg.label}
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              height: 28,
              position: "sticky",
              top: 28,
              zIndex: 2,
              background: "var(--surface)",
              borderBottom: "1px solid var(--hairline)",
            }}
          >
            {(scale === "month"
              ? months.map((m) => ({
                  key: `${m.year}-${m.startOffset}`,
                  label: m.short,
                  days: m.days,
                }))
              : weeks.map((w, i) => ({ key: `w${i}`, label: w.label, days: w.days }))
            ).map((tick) => (
              <div
                key={tick.key}
                style={{
                  width: tick.days * dayWidth,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 8px",
                  fontSize: 10.5,
                  color: "var(--ink-3)",
                  borderRight: "1px solid var(--hairline-soft)",
                  fontFeatureSettings: '"tnum"',
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textTransform: "capitalize",
                }}
              >
                {tick.label}
              </div>
            ))}
          </div>

          {/* Day scale only: a third header row of weekday + day-number cells. */}
          {scale === "day" ? (
            <div
              style={{
                display: "flex",
                height: 24,
                position: "sticky",
                top: 56,
                zIndex: 2,
                background: "var(--surface)",
                borderBottom: "1px solid var(--hairline)",
              }}
            >
              {days.map((d, i) => {
                const wd = (d.getDay() + 6) % 7;
                const weekend = wd >= 5;
                const isToday = i === todayOffset;
                return (
                  <div
                    key={i}
                    style={{
                      width: dayWidth,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1.1,
                      borderRight: "1px solid var(--hairline-soft)",
                      background: weekend ? "var(--surface-soft)" : undefined,
                      color: isToday ? BRAND.orange : weekend ? "var(--ink-4)" : "var(--ink-3)",
                      fontWeight: isToday ? 600 : 500,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    <span style={{ fontSize: 8.5 }}>{DOW[wd]}</span>
                    <span style={{ fontSize: 11 }}>{d.getDate()}</span>
                  </div>
                );
              })}
            </div>
          ) : null}

          <div
            style={{
              position: "absolute",
              top: headerH,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
            }}
          >
            {/* Day scale: subtle weekend bands behind the bars for date texture. */}
            {scale === "day"
              ? days.map((d, i) =>
                  (d.getDay() + 6) % 7 >= 5 ? (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: i * dayWidth,
                        width: dayWidth,
                        background: "var(--surface-soft)",
                      }}
                    />
                  ) : null,
                )
              : null}
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: todayOffset * dayWidth + dayWidth / 2,
                width: 2,
                background: BRAND.orange,
                boxShadow: "0 0 8px rgba(246,129,33,0.3)",
              }}
            />
          </div>

          <div style={{ position: "relative" }}>
            {board.rows.map((r) => {
              const bar = bars.find((b) => b.row.id === r.id);
              return (
                <div
                  key={r.id}
                  style={{
                    height: rowH,
                    position: "relative",
                    borderBottom: "1px solid var(--hairline-soft)",
                  }}
                >
                  {days.map((_, i) =>
                    i % 7 === 0 && i > 0 ? (
                      <div
                        key={i}
                        style={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: i * dayWidth,
                          width: 1,
                          background: "var(--hairline-soft)",
                        }}
                      />
                    ) : null,
                  )}
                  {bar
                    ? (() => {
                        const opt = statusCol?.options?.find((o) => o.id === r.status);
                        const tint = statusColorOf(opt?.color);
                        // Project deadlines can be overdue; a ticket's
                        // creation/lifetime span never is. Judge by the bar's OWN
                        // end (Zeitraum end / due), not r.due — else a Zeitraum
                        // running into the future inherits a stale due's ring.
                        const overdue =
                          !ticketMode &&
                          r.status !== "done" &&
                          bar.offsetDays + bar.span - 1 < todayOffset;
                        const left = bar.offsetDays * dayWidth;
                        const onEnter = (e: MouseEvent<HTMLButtonElement>) =>
                          setHover({ row: r, rect: e.currentTarget.getBoundingClientRect() });
                        const onLeave = () => setHover((h) => (h && h.row.id === r.id ? null : h));

                        // Single-day pin → a diamond milestone marker with the
                        // label flowing right into the open canvas, instead of a
                        // ~60px pill that just truncated the title already shown in
                        // the left column. Only a resolved ticket's creation→
                        // resolved lifetime takes the filled-bar branch below;
                        // open tickets and every project deadline are markers.
                        if (bar.span <= 1) {
                          // The label starts after the column-centre offset +
                          // diamond + gap (left + dayWidth/2 + 13), so cap it
                          // against the canvas right edge from THAT origin — else
                          // a long title overruns minWidth and adds a spurious
                          // horizontal scrollbar over empty canvas.
                          const labelMax = Math.max(
                            80,
                            totalDays * dayWidth - left - dayWidth / 2 - 13,
                          );
                          return (
                            <button
                              onClick={() => onRowClick(r)}
                              onMouseEnter={onEnter}
                              onMouseLeave={onLeave}
                              style={{
                                position: "absolute",
                                left: left + dayWidth / 2 - 6,
                                top: rowH / 2 - 9,
                                height: 18,
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                              }}
                            >
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  flexShrink: 0,
                                  background: tint.dot,
                                  transform: "rotate(45deg)",
                                  borderRadius: 2,
                                  boxShadow: overdue ? "0 0 0 2px var(--danger)" : "none",
                                }}
                              />
                              <span
                                style={{
                                  maxWidth: labelMax,
                                  fontSize: 11.5,
                                  fontWeight: 500,
                                  color: "var(--ink-2)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {r.title}
                              </span>
                            </button>
                          );
                        }

                        const barW = Math.max(10, bar.span * dayWidth - 4);
                        const segs = statusSegments[r.id];
                        // Skip segments when a Zeitraum cell overrides the bar
                        // window — segments are computed over the created→resolved
                        // lifetime, so they'd misalign against a Zeitraum bar.
                        const segmented = Boolean(segs && segs.length > 0 && !r.timeline);
                        return (
                          <button
                            onClick={() => onRowClick(r)}
                            onMouseEnter={onEnter}
                            onMouseLeave={onLeave}
                            style={{
                              position: "absolute",
                              left: left + 2,
                              width: barW,
                              top: 6,
                              height: rowH - 12,
                              // Segmented tickets get per-status coloured slices
                              // below; a plain bar keeps the single status tint.
                              background: segmented ? "var(--surface-soft)" : tint.bg,
                              color: tint.fg,
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "0 8px",
                              overflow: "hidden",
                              boxShadow: overdue ? `inset 0 0 0 1.5px ${tint.dot}` : "none",
                              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                            }}
                          >
                            {segmented ? (
                              (() => {
                                // Map segments onto the bar by the LIFETIME window
                                // (first segment start → last segment end) so they
                                // tile it edge-to-edge with no day-rounding gaps.
                                const list = segs ?? [];
                                const t0 = new Date(list[0]?.start ?? 0).getTime();
                                const t1 = new Date(list[list.length - 1]?.end ?? 0).getTime();
                                const span = Math.max(1, t1 - t0);
                                const px = (iso: string) =>
                                  Math.max(0, Math.min(1, (new Date(iso).getTime() - t0) / span)) *
                                  barW;
                                return list.map((s, si) => {
                                  const l = px(s.start);
                                  const w = Math.max(0, px(s.end) - l);
                                  return (
                                    <div
                                      key={si}
                                      style={{
                                        position: "absolute",
                                        top: 0,
                                        bottom: 0,
                                        left: l,
                                        width: w,
                                        background: statusColorOf(s.color).bg,
                                        zIndex: 0,
                                      }}
                                    />
                                  );
                                });
                              })()
                            ) : (
                              <span
                                style={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: 99,
                                  background: tint.dot,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <span
                              style={{
                                position: "relative",
                                zIndex: 1,
                                fontSize: 11.5,
                                fontWeight: 500,
                                // tint.fg is a FIXED dark hue (never flips) → stays
                                // legible on the fixed light-pastel segment fills in
                                // BOTH themes; var(--ink-1) would go near-white in
                                // dark and vanish (flow_dark_mode_token_rule).
                                color: tint.fg,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r.title}
                            </span>
                          </button>
                        );
                      })()
                    : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {hover
        ? createPortal(
            <TimelineBarTooltip
              row={hover.row}
              rect={hover.rect}
              ticketMode={ticketMode}
              idLabel={ticketLabel(board.ticketPrefix, hover.row.ticketNumber)}
              statusOpt={statusCol?.options?.find((o) => o.id === hover.row.status)}
              segments={statusSegments[hover.row.id]}
            />,
            document.body,
          )
        : null}
    </div>
  );
}

// Portaled hover card for a timeline bar/marker. Display-only (`pointerEvents:
// none`) — the click-through to the full detail panel stays on the bar itself.
// Anchored with fixed positioning off the bar's screen rect; flips below when
// there isn't room above. The card grounds use theme tokens and the status chip
// is the canonical <StatusPill> (.flow-status-tint) so it stays dark-mode-clean
// — a raw tint.bg/tint.fg pill would render a light island on the dark card.
function TimelineBarTooltip({
  row,
  rect,
  ticketMode,
  idLabel,
  statusOpt,
  segments,
}: {
  row: ViewRow;
  rect: DOMRect;
  ticketMode: boolean;
  idLabel: string | null;
  statusOpt: ViewStatusOption | undefined;
  segments: StatusSegment[] | undefined;
}) {
  const W = 280;
  const centerX = rect.left + rect.width / 2;
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1024;
  const left = Math.min(Math.max(centerX, 8 + W / 2), viewportW - 8 - W / 2);
  const above = rect.top > 170;
  const top = above ? rect.top - 10 : rect.bottom + 10;
  const creator = row.raw?.created_by_display_name ?? null;
  const dateIso = ticketMode ? row.createdAt : row.due;
  return (
    <div
      style={{
        position: "fixed",
        left,
        top,
        transform: above ? "translate(-50%, -100%)" : "translate(-50%, 0)",
        width: W,
        zIndex: 60,
        pointerEvents: "none",
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        borderRadius: 10,
        // Neutral alpha so the card lifts off the canvas in BOTH themes (a navy
        // brand shadow disappears on the dark surface).
        boxShadow: "0 8px 28px rgba(0,0,0,0.22)",
        padding: "11px 13px",
        display: "flex",
        flexDirection: "column",
        gap: 7,
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      {idLabel ? <TicketIdChip label={idLabel} /> : null}
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)", lineHeight: 1.35 }}>
        {row.title || "—"}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <StatusPill option={statusOpt ?? null} size="xs" />
        {ticketMode ? (
          <TicketAgeChip createdAt={row.createdAt} resolvedAt={row.resolvedAt} />
        ) : null}
      </div>
      {creator || dateIso ? (
        <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
          {creator ? `${t.createdBy} ${creator}` : ""}
          {creator && dateIso ? " · " : ""}
          {dateIso ? fmtDateLong(dateIso) : ""}
        </div>
      ) : null}
      {segments && segments.length > 1 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            marginTop: 2,
            paddingTop: 7,
            borderTop: "1px solid var(--hairline-soft)",
          }}
        >
          {segments.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 2,
                  background: statusColorOf(s.color).dot,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  color: "var(--ink-2)",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {s.label ?? "—"}
              </span>
              <span style={{ color: "var(--ink-3)", fontFeatureSettings: '"tnum"' }}>
                {fmtSegDur(s.start, s.end)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
